import { useCallback, useMemo, useState } from 'react';
import { ChevronsLeft, ChevronsRight } from 'lucide-react';
import type { DashboardRole } from '@/shared/types/session';
import type { TenantSubtype } from '@propertyscan/shared/types';
import {
    resolveDashboardRoleId,
    resolveRoleLabel,
} from '@/shared/utils/dashboard-role';
import { getSidebarNav, type SidebarNavItem } from '@/shared/utils/sidebar-nav';
import { useAccess, usePermissions, useWebAnalyticsActivation, type ActivationKey } from '@/shared/access';
import { SidebarHeader } from './sidebar-header';
import { WorkspaceCard } from './workspace-card';
import { SidebarNavLink } from './sidebar-nav-link';
import { SidebarNavGroup } from './sidebar-nav-group';
import { SettingsLink } from './settings-link';
import { localStorageUtil } from '@/shared/utils/local-storage';
import { STORAGE_KEYS } from '@/shared/constants/storage-keys';
import { cn } from '@/shared/utils/cn';

interface Props {
    role:       DashboardRole;
    subtype:    TenantSubtype | null;
    tenantName: string;
}

export function Sidebar({ role, subtype, tenantName }: Props) {
    // Collapsed is a per-browser preference, read once so the rail doesn't
    // flash open on every navigation, and written in the click handler (an
    // effect would run twice under StrictMode).
    const [collapsed, setCollapsed] = useState(
        () => localStorageUtil.get<boolean>(STORAGE_KEYS.SIDEBAR_COLLAPSED) ?? false,
    );
    const toggleCollapsed = useCallback(() => {
        setCollapsed((c) => {
            localStorageUtil.set(STORAGE_KEYS.SIDEBAR_COLLAPSED, !c);
            return !c;
        });
    }, []);

    const { can } = useAccess();
    const permissions = usePermissions();
    // Tenant-activated modules — hidden until switched on (see module-activation).
    const { isActive: webAnalyticsActive } = useWebAnalyticsActivation();
    const { roleId, nav } = useMemo(() => {
        const id = resolveDashboardRoleId(role, subtype);
        return { roleId: id, nav: getSidebarNav(id) };
    }, [role, subtype]);

    // Role Matrix visibility: a module the member's role doesn't grant is
    // HIDDEN outright. Plan-blocked modules (owners on a limited plan) stay
    // visible with a lock so the Upgrade popup can upsell.
    const visibleNav = useMemo(
        () => {
            const activation: Record<ActivationKey, boolean> = { web_analytics: webAnalyticsActive };
            const visible = (item: SidebarNavItem): boolean => {
                // A module the tenant hasn't switched on isn't theirs yet: hide
                // it outright — no lock, no upsell, since no upgrade unlocks it.
                if (item.activation && !activation[item.activation]) return false;
                if (!item.feature) return true;
                // `requiresGrant` items need an EXPLICIT grant — owners carry a null
                // permission map (full access) and must not get them for free.
                if (item.requiresGrant) return !!permissions?.[item.feature];
                const result = can(item.feature);
                return result.allowed || result.reason === 'plan';
            };
            return nav
                .filter(visible)
                // A group shows only the children the member may see, and
                // disappears altogether once none are left.
                .map((item) => (item.children ? { ...item, children: item.children.filter(visible) } : item))
                .filter((item) => !item.children || item.children.length > 0);
        },
        [nav, can, permissions, webAnalyticsActive],
    );

    const isAdmin = roleId === 'admin';

    return (
        <aside
            className={cn(
                'flex shrink-0 flex-col border-r border-ink-100 bg-white transition-[width] duration-200',
                collapsed ? 'w-16' : 'w-60',
            )}
        >
            <SidebarHeader collapsed={collapsed} />
            {/* The workspace switcher needs its label to be usable, so the rail
                hides it rather than showing a nameless button. */}
            {!collapsed && <WorkspaceCard tenantName={tenantName} showPlan={!isAdmin} />}

            <nav className={cn('flex-1 overflow-y-auto py-2', collapsed ? 'px-2' : 'px-3')}>
                {!collapsed && (
                    <div className="mb-2 mt-2 px-3 font-ui text-[10px] font-semibold uppercase tracking-wider text-ink-300">
                        {resolveRoleLabel(role, subtype)}
                    </div>
                )}
                <ul className="space-y-0.5">
                    {visibleNav.map((item) => (
                        <li key={item.to + item.label}>
                            {item.children
                                ? <SidebarNavGroup item={item} collapsed={collapsed} />
                                : <SidebarNavLink item={item} collapsed={collapsed} />}
                        </li>
                    ))}
                </ul>
            </nav>

            <div className={cn('border-t border-ink-100', collapsed ? 'p-2' : 'p-3')}>
                {!isAdmin && <SettingsLink collapsed={collapsed} />}
                <button
                    type="button"
                    onClick={toggleCollapsed}
                    title={collapsed ? 'Expand menu' : 'Collapse menu'}
                    aria-label={collapsed ? 'Expand menu' : 'Collapse menu'}
                    aria-expanded={!collapsed}
                    className={cn(
                        'focus-ring mt-0.5 flex w-full items-center gap-3 rounded-lg px-3 py-2 font-ui text-sm text-ink-700 transition hover:bg-ink-50',
                        collapsed && 'justify-center gap-0 px-0',
                    )}
                >
                    {collapsed
                        ? <ChevronsRight className="h-4 w-4 shrink-0" strokeWidth={2} />
                        : <ChevronsLeft className="h-4 w-4 shrink-0" strokeWidth={2} />}
                    {!collapsed && <span>Collapse menu</span>}
                </button>
                {!collapsed && (
                    <div className="px-3 pb-1 pt-3 font-ui text-[10px] text-ink-300">
                        v1.0 · PropertyScan by KMI
                    </div>
                )}
            </div>
        </aside>
    );
}
